import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { cityRoutes } from "../../../mocks/routes";

function DifficultyBadge({ difficulty }: { difficulty: string }) {
  const { t } = useTranslation();
  const config = {
    easy: { label: t("routes_difficulty_easy"), class: "bg-forest-100 text-forest-700" },
    medium: { label: t("routes_difficulty_medium"), class: "bg-amber-100 text-amber-700" },
    hard: { label: t("routes_difficulty_hard"), class: "bg-red-100 text-red-700" },
  }[difficulty] ?? { label: difficulty, class: "bg-stone-100 text-stone-600" };

  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold font-body ${config.class}`}>
      {config.label}
    </span>
  );
}

export default function RoutesSection() {
  const { t } = useTranslation();

  return (
    <section className="py-20 md:py-28 bg-anthracite-900 overflow-hidden">
      {/* Background texture */}
      <div className="absolute inset-0 opacity-5 pointer-events-none">
        <div className="w-full h-full" style={{ backgroundImage: "radial-gradient(circle, #4d8829 1px, transparent 1px)", backgroundSize: "32px 32px" }} />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
          <div>
            <span className="inline-block px-4 py-1.5 rounded-full bg-forest-800/50 text-forest-400 text-xs font-semibold font-body uppercase tracking-wider mb-4">
              {t("routes_badge")}
            </span>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-white">
              {t("routes_title")}
            </h2>
            <p className="text-white/50 text-base mt-2 font-body max-w-xl">
              {t("routes_subtitle")}
            </p>
          </div>
          <Link
            to="/routes"
            className="inline-flex items-center gap-2 text-sm font-semibold text-forest-400 hover:text-forest-300 font-body whitespace-nowrap cursor-pointer"
          >
            {t("routes_view_all")}
            <i className="ri-arrow-right-line" />
          </Link>
        </div>

        {/* Routes Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {cityRoutes.map((route, index) => (
            <div
              key={route.id}
              className={`group relative rounded-2xl overflow-hidden cursor-pointer ${
                index === 0 ? "md:col-span-2 lg:col-span-2" : ""
              }`}
              style={{ minHeight: index === 0 ? "380px" : "280px" }}
            >
              {/* Background */}
              <img
                src={route.image}
                alt={t(route.titleKey)}
                className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-anthracite-900/90 via-anthracite-900/40 to-transparent" />

              {/* Content */}
              <div className="absolute inset-0 p-6 flex flex-col justify-end">
                <div className="flex items-center gap-2 mb-3">
                  <DifficultyBadge difficulty={route.difficulty} />
                  <span className="px-2.5 py-1 rounded-full bg-white/10 backdrop-blur-sm text-white/70 text-xs font-body">
                    {route.category}
                  </span>
                </div>

                <h3 className={`font-display font-bold text-white mb-2 ${index === 0 ? "text-2xl md:text-3xl" : "text-xl"}`}>
                  {t(route.titleKey)}
                </h3>
                <p className="text-white/60 text-sm font-body mb-4 leading-relaxed">
                  {t(route.descKey)}
                </p>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-1.5 text-white/60 text-xs font-body">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-time-line text-sm" />
                      </div>
                      {route.durationHours} {t("routes_hours")}
                    </div>
                    <div className="flex items-center gap-1.5 text-white/60 text-xs font-body">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-map-pin-line text-sm" />
                      </div>
                      {route.stopCount} {t("routes_stops")}
                    </div>
                  </div>

                  <Link
                    to="/routes"
                    className="flex items-center gap-2 px-4 py-2 rounded-xl bg-forest-600 hover:bg-forest-500 text-white text-xs font-semibold font-body transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {t("routes_start")}
                    <div className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-arrow-right-line text-xs" />
                    </div>
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
